import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Combobox } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { TFunction } from 'i18next';
import { FormField } from '../../../components/FormField/FormField';
import { MilesPricingStrategyEnum } from '../../../resources/base/models';
import { quoteSelectors } from '../../../services/redux/features/quote.slice';
import { QuotationFormData } from '../../../types';
import { filterEligiblePromotions } from './utils';

interface PricingStrategySelectorProps {
  promotions: QuoteSchema.Schemas.Promotion[];
  disabled?: boolean;
  onPricingStrategyChange: (
    milesPricingStrategy: QuoteSchema.Schemas.MilesPricingStrategyEnumType,
    deselectedPromotions: Array<string>,
    selectedPromotions?: Array<string>
  ) => void;
}

interface CustomOption {
  textValue: string;
  content: React.ReactNode;
}

type BasePricingStrategyOptionKey = Exclude<QuoteSchema.Schemas.MilesPricingStrategyEnumType, 'PROMOTION'>;
type PromotionalPricingStrategyOptionKey =
  `${Extract<QuoteSchema.Schemas.MilesPricingStrategyEnumType, 'PROMOTION'>}_${string}`;

type PricingStrategyOptionKey = BasePricingStrategyOptionKey | PromotionalPricingStrategyOptionKey;

interface ComboboxOption {
  key: PricingStrategyOptionKey;
  value: string | CustomOption;
  disabled?: boolean;
}

interface ComboboxGroupOption {
  label: string;
  options: ComboboxOption[];
}

const getPromotionIdFromKey = (promotionalOptionPricingKey: PricingStrategyOptionKey): string | undefined => {
  return promotionalOptionPricingKey.split('_')[1];
};

const getPricingStrategyFromKey = (promotionalOptionPricingKey: PricingStrategyOptionKey) => {
  return promotionalOptionPricingKey.split('_')[0] as QuoteSchema.Schemas.MilesPricingStrategyEnumType;
};

const getPricingStrategyKey = (
  pricingStrategy: QuoteSchema.Schemas.MilesPricingStrategyEnumType,
  milesPromotionId?: string
) => {
  return (milesPromotionId ? `${pricingStrategy}_${milesPromotionId}` : pricingStrategy) as PricingStrategyOptionKey;
};

const getPricingOptionsGroups = (eligiblePromotions: QuoteSchema.Schemas.Promotion[], t: TFunction) => {
  const basePricingOptionsGroup: ComboboxGroupOption = {
    label: t('step3:pricing:institutional:label'),
    options: [
      {
        key: getPricingStrategyKey(MilesPricingStrategyEnum.STANDARD),
        value: t('step3:pricing:institutional:standard'),
      },
      {
        key: getPricingStrategyKey(MilesPricingStrategyEnum.BOOSTER),
        value: t('step3:pricing:institutional:booster'),
      },
    ],
  };

  const promotionalPricingOptionsGroup: ComboboxGroupOption = {
    label: t('step3:pricing:promotion:label'),
    options: eligiblePromotions.map((promotion) => ({
      key: getPricingStrategyKey(MilesPricingStrategyEnum.PROMOTION, promotion.milesPromotionId),
      value: promotion.name || '',
    })),
  };

  return [basePricingOptionsGroup, promotionalPricingOptionsGroup];
};

const getCurrentlySelectedOption = (values: QuotationFormData, options: ComboboxGroupOption[]) => {
  return [...options[0].options /* base */, ...options[1].options /* promotional */].find((option) => {
    return (
      getPromotionIdFromKey(option.key) === values.selectedPromotions?.[0] &&
      getPricingStrategyFromKey(option.key) === values.milesPricingStrategy
    );
  });
};

export const PricingStrategyCombobox: FC<PricingStrategySelectorProps> = ({
  promotions,
  disabled = false,
  onPricingStrategyChange,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<QuotationFormData>();
  const promotionsWarningMessages = useSelector(quoteSelectors.selectPromotionsWarningMessages);
  const eligiblePromotions = filterEligiblePromotions(promotions) ?? [];
  const [options, setOptions] = useState<ComboboxGroupOption[]>(getPricingOptionsGroups(eligiblePromotions, t));

  const fieldName = 'milesPricingStrategy';

  useEffect(() => {
    setOptions(getPricingOptionsGroups(eligiblePromotions, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotions]);

  const selectPricingStrategy = (pricingStrategyOptionKey: PricingStrategyOptionKey) => {
    const selectedMilesPromotionId = getPromotionIdFromKey(pricingStrategyOptionKey);
    const selectedMilesPricingStrategy = getPricingStrategyFromKey(pricingStrategyOptionKey);

    const previousSelectedMilesPromotionId = values.selectedPromotions?.[0];

    const pricingSelectionDidNotChange =
      previousSelectedMilesPromotionId === selectedMilesPromotionId &&
      values.milesPricingStrategy === selectedMilesPricingStrategy;

    if (pricingSelectionDidNotChange) return;

    const deselectedPromotions = previousSelectedMilesPromotionId ? [previousSelectedMilesPromotionId] : [];

    if (selectedMilesPromotionId) {
      setFieldValue('milesPricingStrategy', selectedMilesPricingStrategy);
      setFieldValue('deselectedPromotions', deselectedPromotions);
      setFieldValue('selectedPromotions', [selectedMilesPromotionId]);

      onPricingStrategyChange(selectedMilesPricingStrategy!, deselectedPromotions, [selectedMilesPromotionId]);
    } else {
      setFieldValue('milesPricingStrategy', selectedMilesPricingStrategy);
      setFieldValue('deselectedPromotions', deselectedPromotions);
      setFieldValue('selectedPromotions', []);

      onPricingStrategyChange(selectedMilesPricingStrategy!, deselectedPromotions, []);
    }
  };

  const selectedOption = getCurrentlySelectedOption(values, options);
  const defaultState = selectedOption ? { value: [selectedOption] } : undefined;

  return (
    <>
      <FormField
        name={fieldName}
        labelText={t('step3:milesPricingStrategy:label')}
        type="select"
        disabled={disabled}
        render={() => {
          return (
            <Combobox
              id={fieldName}
              onChange={(_selectedOptions) => {
                selectPricingStrategy(_selectedOptions[0].key as PricingStrategyOptionKey);
              }}
              /* key is only a hack to deal with component not updating (as it should) with defaultState or options changes */
              key={`${JSON.stringify(defaultState)}${JSON.stringify(options)}`}
              options={options}
              defaultState={defaultState}
              placeholder={t('step3:pricing:promotion:select')}
              ariaLabelledBy={`${fieldName}-label`}
              disabled={disabled}
              searchable={false}
            />
          );
        }}
      />

      {promotionsWarningMessages.length > 0 && (
        <div className="mt-1">
          {promotionsWarningMessages.map((message) => (
            <p key={`promotion-warning-${message.length}`} className="c-error-message u-mt-xxsmall u-mb-none">
              {message}
            </p>
          ))}
        </div>
      )}
    </>
  );
};
